import { Button, Stack, Typography, Box, IconButton } from '@mui/material';
import { PlaylistAddOutlined, EditOutlined, DeleteOutlined } from '@mui/icons-material';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { EventSlotDto } from '@/types/dtos/events/response/event.dto';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { format, addDays } from 'date-fns';
import { minuteOffsetToTime } from '@/components/events/helpers/minute-offset-to-time.helper';
import { truncateToLength } from '@/helpers/string.helper';
import { useMemo, useState } from 'react';
import { useEventStore } from '@/state/stores/use-event-store';
import { getObjectId } from '@/helpers/object.id.helper';
import { getFlowModerators } from '@/utils/contributor-extraction.utils';
import { Delete } from './actions/delete';
import { EVENT_SLOT_TYPE } from '@/types/dtos/events/event-slot-content.enum';
import { AddEventSlotModal } from './actions/add-event-slot.modal';
import { useDeleteEventSlotMutation } from '@/state/mutations/events/use-delete-event-slot';
import { useNotifications } from '@/hooks/use-notifications';
import ConfirmationModal from '@/components/common/confirmation-modal.tsx';
import useEventMode from '@/components/events/hooks/use-event-mode';
import { isOrganizeMode } from '@/components/events/helpers/mode.helper';

const MAX_MODERATORS_LABEL_LENGTH = 45; // this fits in at most 2 lines

export const Title = ({ flow }: { flow: FlowDto }) => {
  const navigate = useNavigate();
  const { event, contributors } = useEventStore();
  const { t } = useTranslation();
  const notification = useNotifications();
  const mode = useEventMode();
  const [addEventSlotOpen, setAddEventSlotOpen] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<EventSlotDto | null>(null);
  const { mutateAsync: deleteEventSlotMutation, isPending: isDeletingSlot } = useDeleteEventSlotMutation();

  // Find all eventSlots for this flow
  const eventSlotsForFlow = useMemo(() => {
    if (!event?.slots || !flow) return [];

    return event.slots.filter(
      (slot: EventSlotDto) =>
        slot.deleted !== true &&
        slot.contentType === EVENT_SLOT_TYPE.FLOW &&
        (slot.content === flow.id ||
          slot.content === flow.origin ||
          (typeof flow.origin === 'object' && flow.origin?.id && slot.content === flow.origin.id)),
    );
  }, [event, flow]);

  // Flow details: Only moderators
  const flowDetails = useMemo(() => {
    const moderators = getFlowModerators(flow, contributors || [], event?.multiFlow);
    const moderatorNames = moderators.map((c) => c.profiles[0]?.displayName).filter(Boolean);

    if (moderatorNames?.length) {
      const moderatorsText = truncateToLength(moderatorNames.join(', '), MAX_MODERATORS_LABEL_LENGTH);
      return moderatorsText;
    }

    return t('common.N/A');
  }, [flow, event, contributors, t]);

  // Format eventSlot display: Day | Time Range | Duration
  const formatEventSlot = (slot: EventSlotDto): string => {
    const parts: string[] = [];

    // Date part: 28.02.
    if (event?.startDate && slot.day?.id) {
      const dayIndex = event.days.findIndex((day) => day.id === slot.day.id);
      if (dayIndex !== -1) {
        const flowDate = addDays(new Date(event.startDate), dayIndex);
        parts.push(format(flowDate, 'dd.MM.'));
      }
    }

    // Determine duration: use plannedDuration if set, otherwise use computedDuration
    // Priority: slot.durationPlannedMinutes > flow.durationPlannedMinutes > flow.computedDurationMinutes
    const durationMinutes = slot.durationPlannedMinutes ?? flow.durationPlannedMinutes ?? flow.computedDurationMinutes;

    // Time range part: 11.00-22.00
    if (slot.startOffsetMinutes !== undefined && durationMinutes !== undefined) {
      const startTime = minuteOffsetToTime(slot.startOffsetMinutes);
      const endTime = minuteOffsetToTime(slot.startOffsetMinutes + durationMinutes);
      parts.push(`${startTime}-${endTime}`);
    }

    // Duration part (if available)
    if (durationMinutes !== undefined) {
      const hours = Math.floor(durationMinutes / 60);
      const minutes = durationMinutes % 60;
      if (hours > 0 && minutes > 0) {
        parts.push(`${hours}h ${minutes}m`);
      } else if (hours > 0) {
        parts.push(`${hours}h`);
      } else if (minutes > 0) {
        parts.push(`${minutes}m`);
      }
    }

    return parts.join(' | ') || t('common.N/A');
  };

  const handleStudioClick = (eventSlotId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const flowId = flow?.origin ? getObjectId(flow.origin) : flow.id;
    const eventId = event?.origin ? getObjectId(event.origin) : event?.id;
    if (eventId) {
      navigate(`/flow/${flowId}?eventSlotId=${eventSlotId}&contextType=event&contextId=${eventId}`);
    } else {
      navigate(`/flow/${flowId}?eventSlotId=${eventSlotId}`);
    }
  };

  const handleFlowOpenWithoutSlot = (e: React.MouseEvent) => {
    e.stopPropagation();
    const flowId = flow?.origin ? getObjectId(flow.origin) : flow.id;
    const eventId = event?.origin ? getObjectId(event.origin) : event?.id;
    if (eventId) {
      navigate(`/flow/${flowId}?contextType=event&contextId=${eventId}`);
    } else {
      navigate(`/flow/${flowId}`);
    }
  };

  const handleDeleteSlotClick = (slot: EventSlotDto, e: React.MouseEvent) => {
    e.stopPropagation();
    setSlotToDelete(slot);
  };

  const handleDeleteSlotCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setSlotToDelete(null);
  };

  const handleDeleteSlotConfirm = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (!slotToDelete || !event?.id) return;
    if (isDeletingSlot) return;

    try {
      await deleteEventSlotMutation({ eventId: event.id, slotId: slotToDelete.id });
      setSlotToDelete(null);
      notification.notifySuccess(
        t('flows.deleteEventSlot.notifications.success', { defaultValue: 'Event slot deleted.' }),
      );
    } catch (error) {
      console.error('Event slot deletion error:', error);
      notification.notifyError(
        t('flows.deleteEventSlot.notifications.error', { defaultValue: 'Failed to delete event slot.' }),
      );
    }
  };

  return (
    <Stack spacing={1} sx={{ flex: 1 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
        <Stack sx={{ flex: 1 }}>
          <Typography variant="body2">{flow.title}</Typography>
          <Typography variant="subtitle1" color="textDisabled">
            {flowDetails}
          </Typography>
        </Stack>
        <IconButton
          size="small"
          onClick={handleFlowOpenWithoutSlot}
          sx={{
            width: '24px',
            height: '24px',
            padding: '4px',
          }}
          aria-label={t('flows.openWithoutSlot.ariaLabel', 'Open flow without slot')}
        >
          <EditOutlined fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAddEventSlotOpen(true);
          }}
          sx={{
            width: '24px',
            height: '24px',
            padding: '4px',
          }}
          aria-label={t('flows.addEventSlot.ariaLabel', 'Add another event slot')}
        >
          <PlaylistAddOutlined fontSize="small" />
        </IconButton>
        <Delete flow={flow} />
      </Stack>

      {/* EventSlots List */}
      {eventSlotsForFlow.length > 0 ? (
        <Box sx={{ pl: 2, borderLeft: 1, borderColor: 'divider' }}>
          <Stack spacing={0.5}>
            {eventSlotsForFlow.map((slot) => (
              <Stack
                key={slot.id}
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  'py': 0.5,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                  },
                }}
              >
                <Typography variant="caption" color="textDisabled" sx={{ flex: 1 }}>
                  {formatEventSlot(slot)}
                </Typography>
                <Button
                  color="info"
                  size="small"
                  variant="contained"
                  onClick={(e) => handleStudioClick(slot.id, e)}
                  sx={{
                    minWidth: 'unset',
                    padding: '2px 8px',
                  }}
                >
                  {t('common.studio')}
                </Button>
                <IconButton
                  size="small"
                  onClick={(e) => handleDeleteSlotClick(slot, e)}
                  disabled={isOrganizeMode(mode) || !event?.id}
                  sx={{
                    width: '24px',
                    height: '24px',
                    padding: '4px',
                  }}
                  aria-label={t('flows.deleteEventSlot.ariaLabel', { defaultValue: 'Delete event slot' })}
                >
                  <DeleteOutlined fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
        </Box>
      ) : (
        <Box sx={{ pl: 2, borderLeft: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="textDisabled">
            {t('flows.noScheduledSlots', 'No scheduled slots')}
          </Typography>
        </Box>
      )}
      <AddEventSlotModal open={addEventSlotOpen} setOpen={setAddEventSlotOpen} flow={flow} />
      <ConfirmationModal
        open={!!slotToDelete}
        isLoading={isDeletingSlot}
        title={t('flows.deleteEventSlot.title', { defaultValue: 'Delete event slot' })}
        text={t('flows.deleteEventSlot.text', {
          defaultValue: `Delete ${slotToDelete ? formatEventSlot(slotToDelete) : 'this event slot'}?`,
        })}
        onConfirm={handleDeleteSlotConfirm}
        onCancel={handleDeleteSlotCancel}
      />
    </Stack>
  );
};
